import { z } from 'zod'

import { ChannelStatus } from 'common/core/interfaces/channelStatuses'
import { defaultReplySchema } from 'shared/api/common/schemas/flow/defaultReply'

export enum RegistrationFlowStep {
  NONE = 'none',
  SIGN_UP_PENDING = 'sign_up_pending',
  VERIFICATION_PENDING = 'verification_pending',
  ACTIVE = 'active',
}

export enum WaAccountNameDecision {
  APPROVED = 'APPROVED',
  DEFERRED = 'DEFERRED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export enum WaAccountReviewStatus {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export enum WaBusinessIndustry {
  AUTO = 'Automotive',
  BEAUTY = 'Beauty, Spa and Salon',
  APPAREL = 'Clothing and Apparel',
  EDU = 'Education',
  ENTERTAIN = 'Entertainment',
  ONLINE_GAMBLING = 'Online Gambling & Gaming',
  PHYSICAL_GAMBLING = 'Non-Online Gambling & Gaming (E.g. Brick and mortar)',
  EVENT_PLAN = 'Event Planning and Service',
  FINANCE = 'Finance and Banking',
  GROCERY = 'Food and Grocery',
  ALCOHOL = 'Alcoholic Beverages',
  GOVT = 'Public Service',
  HOTEL = 'Hotel and Lodging',
  HEALTH = 'Medical and Health',
  OTC_DRUGS = 'Over-the-Counter Drugs',
  NONPROFIT = 'Non-profit',
  PROF_SERVICES = 'Professional Services',
  RETAIL = 'Shopping and Retail',
  TRAVEL = 'Travel and Transportation',
  RESTAURANT = 'Restaurant',
  OTHER = 'Other',
}

export enum WaBusinessVerificationStatus {
  MORE_INFORMATION_REQUESTED = 'MORE_INFORMATION_REQUESTED',
  BUSINESS_VERIFIED = 'BUSINESS_VERIFIED',
  BUSINESS_REJECTED = 'BUSINESS_REJECTED',
  BUSINESS_REVOKED = 'BUSINESS_REVOKED',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  PENDING = 'pending',
  REVOKED = 'revoked',
  NOT_VERIFIED = 'not_verified',
  PENDING_SUBMISSION = 'pending_submission',
  PENDING_NEED_MORE_INFO = 'pending_need_more_info',
}

export enum WaCoexistenceImportContactsPeriod {
  ONE_DAY = 1,
  THREE_MONTHS = 90,
  SIX_MONTHS = 180,
}

export enum WaCoexistenceImportContactsState {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum WaMessagingLimitTier {
  TIER_50 = 'TIER_50',
  TIER_250 = 'TIER_250',
  TIER_1K = 'TIER_1K',
  TIER_10K = 'TIER_10K',
  TIER_100K = 'TIER_100K',
  TIER_UNLIMITED = 'TIER_UNLIMITED',
}

export enum WaMetaPhoneStatus {
  PENDING = 'PENDING',
  DELETED = 'DELETED',
  MIGRATED = 'MIGRATED',
  BANNED = 'BANNED',
  RESTRICTED = 'RESTRICTED',
  RATE_LIMITED = 'RATE_LIMITED',
  FLAGGED = 'FLAGGED',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  UNKNOWN = 'UNKNOWN',
  UNVERIFIED = 'UNVERIFIED',
}

export enum WaMMLiteStatus {
  PENDING = 'PENDING',
  ONBOARDED = 'ONBOARDED',
}

export const channelData = z.object({
  account_review_status: z.nativeEnum(WaAccountReviewStatus).nullish(),
  bought_phone: z
    .object({
      country_code: z.string(), // US, RU, etc
      friendly_number: z.string().optional(), // without country code
      phone_number: z.string(),
    })
    .nullable(),
  business_account_name: z.string().optional(),
  business_id: z.string().optional(),
  business_verification_status: z.nativeEnum(WaBusinessVerificationStatus).nullish(),
  coexistence_import_contacts_period: z.nativeEnum(WaCoexistenceImportContactsPeriod).nullish(),
  coexistence_import_contacts_state: z.nativeEnum(WaCoexistenceImportContactsState).nullish(),
  country_code: z.string().optional(),
  default_reply: defaultReplySchema.nullish(),
  has_credit_line: z.boolean().nullish(),
  has_issue_with_credit_line: z.boolean().nullish(),
  icebreakers_whatsapp_enabled: z.boolean().optional(),
  is_banned: z.boolean().optional(),
  is_cbp_enabled: z.boolean().optional(),
  is_channel_enabled: z.boolean().optional(),
  is_coexistence_used: z.boolean().optional(),
  is_exceeded_free_limits: z.boolean().optional(),
  messaging_limit_tier: z.nativeEnum(WaMessagingLimitTier).nullish(),
  meta_phone_status: z.nativeEnum(WaMetaPhoneStatus).nullish(),
  mm_lite_status: z.nativeEnum(WaMMLiteStatus).nullish(),
  new_business_account_name: z.string().nullish(),
  new_business_account_name_decision: z.nativeEnum(WaAccountNameDecision).nullish(),
  new_business_account_name_expires_at: z.number().nullish(),
  phone_number: z.string().optional(),
  photo: z.string().nullish(),
  settings: z
    .object({
      address: z.string().optional(),
      description: z.string().optional(),
      email: z.string().optional(),
      vertical: z.nativeEnum(WaBusinessIndustry).optional(),
      websites: z.array(z.string()).optional(),
    })
    .optional()
    .or(z.tuple([])),
  // registration flow "step" during registration
  state: z.nativeEnum(RegistrationFlowStep).optional(),
  // actual status of the channel
  status: z.nativeEnum(ChannelStatus).optional(),
  ts_channel_connected: z.string().nullish(),
  verification: z
    .object({
      expired_at: z.string(),
      url: z.string(),
    })
    .optional(),
  wa_user_disconnect_channel: z.boolean().optional(),
  waba_id: z.number().nullish(),
})

// data that we get from the backend during parsing __INIT__ or from the getChannelData request
export type WhatsAppChannelData = z.infer<typeof channelData>

export interface WhatsAppUnconnectedChannelState {
  state: ChannelStatus.NONE
  bought_phone: null
}

export type WaMMLiteModalType = 'one_step' | 'two_step' | null

// frontend fields that we don't get from the backend and use for internal purposes
export interface WhatsAppChannelStoreSpecifics {
  loading?: boolean
  error?: boolean
  isConfirmNewNameError?: boolean
  isMMLiteLoading?: boolean
  isMMLiteError?: boolean
  mmOpenModalType?: WaMMLiteModalType
}

export type WhatsAppChannelState = WhatsAppUnconnectedChannelState | WhatsAppConnectedState
export type WhatsAppConnectedState = Partial<WhatsAppChannelData> & WhatsAppChannelStoreSpecifics
